import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Heart, X, Star, MapPin, RefreshCw, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';

function CompatRadar({ profile }) {
  const traits = ['Social', 'Aventure', 'Culture', 'Sport', 'Humour', 'Romance'];
  const data = traits.map(trait => ({
    trait,
    vous: Math.floor(Math.random() * 40 + 50),
    match: Math.floor(Math.random() * 40 + 50),
  }));
  const score = Math.floor(data.reduce((s, d) => s + (100 - Math.abs(d.vous - d.match)), 0) / data.length);

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold flex items-center gap-2" style={{color:'var(--text-muted)'}}>
          <Sparkles size={14} className="text-brand-violet" /> Compatibilite
        </h3>
        <span className={`text-lg font-black ${score > 75 ? 'text-green-400' : score > 50 ? 'text-amber-400' : 'text-red-400'}`}>{score}%</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <RadarChart data={data}>
          <PolarGrid stroke="var(--border-color)" />
          <PolarAngleAxis dataKey="trait" tick={{fill:'var(--text-muted)', fontSize:10}} />
          <Radar name="Vous" dataKey="vous" stroke="#A78BFA" fill="#A78BFA" fillOpacity={0.15} strokeWidth={1.5} />
          <Radar name={profile?.name} dataKey="match" stroke="#FF6B9D" fill="#FF6B9D" fillOpacity={0.15} strokeWidth={1.5} />
        </RadarChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-4 mt-1">
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-brand-violet" /><span className="text-[10px]" style={{color:'var(--text-muted)'}}>Vous</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-brand-pink" /><span className="text-[10px]" style={{color:'var(--text-muted)'}}>{profile?.name}</span></div>
      </div>
    </div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(null);
  const [matchPopup, setMatchPopup] = useState(null);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/discover?max_distance=50&min_age=18&max_age=50');
      setProfiles(res.data || []);
      setCurrent(0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProfiles(); }, []);

  const handleSwipe = async (direction) => {
    if (!profiles[current]) return;
    setSwiping(direction);
    try {
      const res = await api.post('/swipe', { target_user_id: profiles[current].id, direction });
      if (res.data?.matched) {
        setMatchPopup(profiles[current]);
        setTimeout(() => setMatchPopup(null), 3000);
      }
    } catch (e) { console.error(e); }
    setTimeout(() => {
      setSwiping(null);
      setCurrent(c => c + 1);
    }, 300);
  };

  const profile = profiles[current];
  const hasMore = current < profiles.length;

  return (
    <div className="h-full flex flex-col" data-testid="discover-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>Decouvrir</h1>
        <p className="text-sm mt-1" style={{color:'var(--text-muted)'}}>Trouvez votre match parfait</p>
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        {loading ? (
          <div className="w-10 h-10 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" />
        ) : !hasMore ? (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{background:'var(--bg-card)'}}>
              <Heart size={32} style={{color:'var(--text-faint)'}} />
            </div>
            <p className="text-lg font-semibold mb-2" style={{color:'var(--text-secondary)'}}>Plus de profils pour le moment</p>
            <p className="text-sm mb-6" style={{color:'var(--text-muted)'}}>Revenez plus tard ou elargissez vos criteres</p>
            <button onClick={loadProfiles} className="btn-primary flex items-center gap-2 mx-auto" data-testid="refresh-btn">
              <RefreshCw size={16} /> Actualiser
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 items-start max-w-5xl w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={profile?.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1, x: swiping === 'left' ? -200 : swiping === 'right' ? 200 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="glass-card overflow-hidden w-full lg:w-[360px] shrink-0"
              >
                <div className="relative aspect-[3/4]" style={{background:'var(--bg-card)'}}>
                  {profile?.photos?.[0] ? (
                    <img src={profile.photos[0]} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl font-black" style={{color:'var(--text-faint)'}}>{profile?.name?.[0]}</span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <h2 className="text-2xl font-black text-white">{profile?.name}, {profile?.age}</h2>
                    {profile?.city && (
                      <p className="text-sm text-white/60 flex items-center gap-1 mt-1"><MapPin size={14} /> {profile.city}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4 p-5">
                  <button onClick={() => handleSwipe('left')}
                    className="w-14 h-14 rounded-full flex items-center justify-center hover:bg-red-500/20 transition-all hover:scale-110"
                    style={{background:'var(--bg-card)', border:'1px solid var(--border-color)'}} data-testid="swipe-left-btn">
                    <X size={24} className="text-red-400" />
                  </button>
                  <button onClick={() => handleSwipe('right')}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-pink to-rose-500 flex items-center justify-center hover:scale-110 transition-transform shadow-lg shadow-brand-pink/20"
                    data-testid="swipe-right-btn">
                    <Heart size={28} className="text-white" />
                  </button>
                  <button onClick={() => handleSwipe('right')}
                    className="w-14 h-14 rounded-full flex items-center justify-center hover:bg-blue-500/20 transition-all hover:scale-110"
                    style={{background:'var(--bg-card)', border:'1px solid var(--border-color)'}} data-testid="super-like-btn">
                    <Star size={24} className="text-blue-400" />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>

            {profile && (
              <div className="flex-1 space-y-4 min-w-0">
                {/* Compatibility Radar - Web Exclusive */}
                <CompatRadar profile={profile} />

                {profile.bio && (
                  <div className="glass-card p-5">
                    <h3 className="text-sm font-semibold mb-2" style={{color:'var(--text-muted)'}}>A propos</h3>
                    <p className="text-sm leading-relaxed" style={{color:'var(--text-secondary)'}}>{profile.bio}</p>
                  </div>
                )}
                {profile.interests?.length > 0 && (
                  <div className="glass-card p-5">
                    <h3 className="text-sm font-semibold mb-3" style={{color:'var(--text-muted)'}}>Centres d'interet</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.interests.map(i => (
                        <span key={i} className="px-3 py-1.5 rounded-full bg-brand-violet/15 text-brand-violet text-xs font-medium">{i}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold mb-2" style={{color:'var(--text-muted)'}}>Informations</h3>
                  <div className="space-y-2 text-sm">
                    {profile.relationship_goal && <p style={{color:'var(--text-secondary)'}}>Recherche : <span style={{color:'var(--text-primary)'}}>{profile.relationship_goal}</span></p>}
                    {profile.height && <p style={{color:'var(--text-secondary)'}}>Taille : <span style={{color:'var(--text-primary)'}}>{profile.height} cm</span></p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Match Popup */}
      <AnimatePresence>
        {matchPopup && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <motion.div initial={{scale:0.5}} animate={{scale:1}} className="text-center">
              <h1 className="text-5xl font-black gradient-text mb-4">C'est un Match !</h1>
              <p className="text-xl text-white/70 mb-6">Vous et {matchPopup.name} vous etes aimes</p>
              <Heart size={64} className="text-brand-pink mx-auto animate-pulse" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
